import { useMemo } from "react";
import { Loader2, Sun, Moon, Calendar, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExpandablePanel } from "./ExpandablePanel";
import { LocationIndicator } from "./LocationIndicator";
import { UtsavaListMini } from "./UtsavaListMini";
import { UtsavaSpanEnrichment } from "./UtsavaEnrichment";
import { buildUtsavaSpans } from "@/lib/panchanga-grid";
import { findUtsavaToday, sadhanaOneLiner } from "@/lib/panchanga-utsava";
import {
  libraryLinkForFestival,
  libraryLinkForFestivalName,
  libraryLinkForVrata,
  libraryLinkForVrataName,
  libraryImageForFestival,
  libraryImageForVrata,
} from "@/lib/sacred-library/utils/panchanga-links";
import { LibraryTopicLink } from "@/components/sacred-library/LibraryTopicLink";
import { FestivalImage } from "@/components/sacred-library/FestivalImage";
import { SacredImage } from "@/components/sacred-library/SacredImage";
import { MantraAudioIcon } from "@/components/audio/MantraAudioIcon";
import { TriLanguageBlock } from "@/components/i18n/TriLanguageBlock";
import { toTriFromFields } from "@/lib/i18n/tri-language";

const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function DashboardPanchangaCard({
  today,
  tomorrow,
  monthData,
  loc,
  meta,
  locMessage,
  locLoading,
  panchangaLoading,
  onDetectAuto,
  onSaveProfile,
  onManualApply,
  locSaving,
}) {
  const monthName = monthData?.month ? MONTH_NAMES[monthData.month] : "";
  const utsavaSpans = useMemo(
    () => (monthData?.days ? buildUtsavaSpans(monthData.days, monthName) : []),
    [monthData, monthName]
  );
  const utsavaToday = useMemo(
    () => (today?.date && monthData?.days ? findUtsavaToday(monthData.days, today.date, monthName) : null),
    [today, monthData, monthName]
  );

  const sadhanaLine = sadhanaOneLiner(today);
  const festivalToday = today?.festivals?.[0];
  const vrataToday = today?.vratas?.[0];
  const festivalLibLink = festivalToday
    ? libraryLinkForFestival(festivalToday.id) || libraryLinkForFestivalName(festivalToday.name_en)
    : null;
  const vrataLibLink = vrataToday
    ? libraryLinkForVrata(vrataToday.id) || libraryLinkForVrataName(vrataToday.name_en)
    : null;
  const festivalImageUrl = festivalToday
    ? libraryImageForFestival(festivalToday.id)
    : null;
  const vrataImageUrl = vrataToday ? libraryImageForVrata(vrataToday.id) : null;

  if (locLoading || panchangaLoading) {
    return (
      <Card className="mb-8 bg-white/90 shadow-card" data-testid="dashboard-panchanga-loading">
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!today) return null;

  return (
    <Card className="mb-8 bg-gradient-to-br from-primary/5 to-secondary/5 shadow-card" data-testid="dashboard-dharma-summary">
      <CardHeader>
        <CardTitle className="font-heading text-xl">Today&apos;s Dharma Summary</CardTitle>
        <CardDescription>Hybrid pañchāṅga at your location (sunrise-bound)</CardDescription>
        <LocationIndicator loc={loc} meta={meta} compact />
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Compact rows */}
        <div className="grid gap-2 sm:grid-cols-2">
          <CompactRow label="Tithi" value={`${today.tithi?.name} (${today.paksha?.name_localized || today.paksha?.name})`} />
          <CompactRow label="Nakṣatra" value={today.nakshatra?.name} />
          <CompactRow label="Sun" value={`↑ ${today.sunrise} · ↓ ${today.sunset}`} icon={Sun} />
          <CompactRow
            label="Moon"
            value={`↑ ${today.moonrise_local ?? "—"} · ↓ ${today.moonset_local ?? "—"}`}
            icon={Moon}
          />
          {festivalToday && (
            <CompactRow
              label="Festival"
              value={festivalToday.name_en}
              icon={Calendar}
              highlight
              imageUrl={festivalImageUrl}
              imageVariant="festival"
            />
          )}
          {vrataToday && (
            <CompactRow
              label="Vrata"
              value={vrataToday.name_en}
              highlight
              imageUrl={vrataImageUrl}
              imageVariant="vrata"
            />
          )}
          {sadhanaLine && <CompactRow label="Sādhana" value={sadhanaLine} className="sm:col-span-2" />}
          {today.mantra && (
            <CompactRow
              label="Mantra"
              value={
                <span className="inline-flex items-center gap-2">
                  <span className="text-primary">{today.mantra}</span>
                  <MantraAudioIcon mantra={today.mantra} />
                </span>
              }
              className="sm:col-span-2"
            />
          )}
        </div>

        {utsavaToday && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-xs">
              <Star className="w-3 h-3 mr-1" />
              Utsava Today: {utsavaToday.name} (Day {utsavaToday.dayNumber} of {utsavaToday.totalDays})
            </Badge>
          </div>
        )}

        {/* Expandable details */}
        <div className="space-y-2 pt-2">
          <ExpandablePanel
            title="Tithi details"
            summary={today.tithi?.full_name}
            testId="expand-tithi"
          >
            <DetailBlock label="Name" value={today.tithi?.full_name} />
            <DetailBlock label="Pakṣa" value={today.paksha?.name_localized} />
            <DetailBlock label="Ends (local)" value={today.tithi?.ends_at_local_hm} />
            {today.do_and_dont?.do?.length > 0 && (
              <div>
                <p className="font-medium text-foreground text-xs">Do</p>
                <ul className="list-disc pl-4">
                  {today.do_and_dont.do.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            )}
            {today.fasting_rules && (
              <div>
                <p className="font-medium text-foreground text-xs">Fasting</p>
                {Object.entries(today.fasting_rules).map(([k, v]) => (
                  <p key={k}>
                    <span className="capitalize">{k.replace(/_/g, " ")}: </span>
                    {v}
                  </p>
                ))}
              </div>
            )}
          </ExpandablePanel>

          <ExpandablePanel title="Nakṣatra details" summary={today.nakshatra?.name} testId="expand-nakshatra">
            <DetailBlock label="Name" value={today.nakshatra?.name} />
            <DetailBlock label="Ends (local)" value={today.nakshatra?.ends_at_local_hm} />
            <DetailBlock label="Deity" value={today.nakshatra?.deity} />
            <p className="text-xs italic">Qualities and actions follow your lineage and the nakṣatra pada — consult your guru for muhūrta work.</p>
          </ExpandablePanel>

          {festivalToday && (
            <ExpandablePanel title="Festival" summary={festivalToday.name_en} testId="expand-festival">
              {festivalImageUrl && (
                <FestivalImage src={festivalImageUrl} alt={festivalToday.name_en} size="detail" className="mb-2 max-h-32" />
              )}
              {festivalLibLink && (
                <div className="mb-2">
                  <LibraryTopicLink to={festivalLibLink}>Full festival guide</LibraryTopicLink>
                </div>
              )}
              <DetailBlock label="Festival" value={festivalToday.name_en} />
              {festivalToday.name_te && <DetailBlock label="Telugu" value={festivalToday.name_te} />}
              {festivalToday.notes && <DetailBlock label="Note" value={festivalToday.notes} />}
              {today.story && <DetailBlock label="Story" value={today.story} />}
              {today.puja_vidhi?.general?.map((line, i) => (
                <p key={i}>
                  {line}
                </p>
              ))}
              {today.mantra && <DetailBlock label="Mantra" value={today.mantra} />}
              {today.vedic_history_note && <DetailBlock label="History" value={today.vedic_history_note} />}
            </ExpandablePanel>
          )}

          {vrataToday && (
            <ExpandablePanel title="Vrata" summary={vrataToday.name_en} testId="expand-vrata">
              {vrataImageUrl && (
                <SacredImage src={vrataImageUrl} alt={vrataToday.name_en} variant="vrata" size="detail" className="mb-2 max-h-32" />
              )}
              {vrataLibLink && (
                <div className="mb-2">
                  <LibraryTopicLink to={vrataLibLink}>Full vrata guide</LibraryTopicLink>
                </div>
              )}
              <DetailBlock label="Vrata" value={vrataToday.name_en} />
              {today.vrata?.vrata && <DetailBlock label="Practice" value={today.vrata.vrata} />}
              {today.fasting_rules && (
                <div>
                  {Object.entries(today.fasting_rules).map(([k, v]) => (
                    <p key={k}>
                      <span className="capitalize">{k.replace(/_/g, " ")}: </span>
                      {v}
                    </p>
                  ))}
                </div>
              )}
              <DetailBlock label="Tithi ends" value={today.tithi?.ends_at_local_hm} />
            </ExpandablePanel>
          )}

          {today.sadhana_guide && (
            <ExpandablePanel title="Sādhana" summary={sadhanaLine} testId="expand-sadhana">
              {Object.entries(today.sadhana_guide).map(([k, v]) => (
                <p key={k}>
                  <span className="font-medium text-foreground capitalize">{k.replace(/_/g, " ")}: </span>
                  {v}
                </p>
              ))}
              {today.mantra && <DetailBlock label="Mantra" value={today.mantra} />}
            </ExpandablePanel>
          )}

          {today.mantra && (
            <ExpandablePanel title="Mantra" summary={today.mantra.slice(0, 48)} testId="expand-mantra">
              <p className="text-primary text-base">{today.mantra}</p>
              <p className="text-xs">Repeat with steady breath; meaning unfolds through japa over time.</p>
            </ExpandablePanel>
          )}

          {utsavaToday && (
            <ExpandablePanel
              title={`Utsava: ${utsavaToday.name}`}
              summary={`Day ${utsavaToday.dayNumber} of ${utsavaToday.totalDays}`}
              testId="expand-utsava-today"
            >
              <UtsavaSpanEnrichment sourceDay={utsavaToday.sourceDay} />
            </ExpandablePanel>
          )}
        </div>

        {tomorrow && (
          <div className="p-3 rounded-lg bg-muted/30 border border-border/40" data-testid="dashboard-tomorrow-preview">
            <p className="text-sm font-medium mb-1">Tomorrow</p>
            <ul className="text-xs text-muted-foreground space-y-0.5">
              <li>
                Tithi: {tomorrow.tithi?.name} ({tomorrow.paksha?.name_localized || tomorrow.paksha?.name})
              </li>
              {tomorrow.festivals?.[0] && (
                <li>
                  Festival: {tomorrow.festivals[0].name_en}{" "}
                  {(libraryLinkForFestival(tomorrow.festivals[0].id) ||
                    libraryLinkForFestivalName(tomorrow.festivals[0].name_en)) && (
                    <LibraryTopicLink
                      to={
                        libraryLinkForFestival(tomorrow.festivals[0].id) ||
                        libraryLinkForFestivalName(tomorrow.festivals[0].name_en)
                      }
                      className="ml-1"
                    />
                  )}
                </li>
              )}
              {tomorrow.vratas?.[0] && (
                <li>
                  Vrata: {tomorrow.vratas[0].name_en}{" "}
                  {(libraryLinkForVrata(tomorrow.vratas[0].id) ||
                    libraryLinkForVrataName(tomorrow.vratas[0].name_en)) && (
                    <LibraryTopicLink
                      to={
                        libraryLinkForVrata(tomorrow.vratas[0].id) ||
                        libraryLinkForVrataName(tomorrow.vratas[0].name_en)
                      }
                      className="ml-1"
                    />
                  )}
                </li>
              )}
              {!tomorrow.festivals?.length && !tomorrow.vratas?.length && (
                <li className="italic">No major festival or vrata flagged at sunrise.</li>
              )}
            </ul>
          </div>
        )}

        <UtsavaListMini spans={utsavaSpans} />

        <LocationIndicator
          loc={loc}
          meta={meta}
          message={locMessage}
          onDetectAuto={onDetectAuto}
          onSaveProfile={onSaveProfile}
          onManualApply={onManualApply}
          saving={locSaving}
        />
      </CardContent>
    </Card>
  );
}

function CompactRow({ label, value, icon: Icon, highlight, className = "", imageUrl, imageVariant }) {
  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-md ${highlight ? "bg-secondary/10" : "bg-white/60"} ${className}`}
    >
      {imageUrl ? (
        imageVariant === "festival" ? (
          <FestivalImage src={imageUrl} alt={value} size="thumb" className="w-10 h-10 flex-shrink-0" />
        ) : (
          <SacredImage
            src={imageUrl}
            alt={value}
            variant={imageVariant || "vrata"}
            size="thumb"
            className="w-10 h-10 flex-shrink-0"
          />
        )
      ) : (
        Icon && <Icon className="w-4 h-4 text-primary shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        {typeof value === "string" || typeof value === "number" ? (
          <p className="text-sm font-medium truncate">{value}</p>
        ) : (
          <div className="text-sm font-medium">{value}</div>
        )}
      </div>
    </div>
  );
}

function DetailBlock({ label, value, meaningPrefix = "description", token }) {
  if (!value) return null;
  if (typeof value === "string" && value.length > 80) {
    const triItem =
      meaningPrefix === "description"
        ? toTriFromFields({ english: value, description_en: value })
        : toTriFromFields({ english: value, translation_en: value });
    return (
      <div className="mb-2">
        <p className="font-medium text-foreground text-xs mb-1">{label}</p>
        <TriLanguageBlock item={triItem} meaningPrefix={meaningPrefix} compact token={token} />
      </div>
    );
  }
  return (
    <p>
      <span className="font-medium text-foreground">{label}: </span>
      {value}
    </p>
  );
}
